import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import TurnstileWidget from "@/components/TurnstileWidget";
import { Mail, Lock, User, Building, ArrowRight, Check, Key, Gift } from "lucide-react";
import { getStoredReferralCode, clearStoredReferralCode } from "@/pages/ReferralCapturePage";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const claimCodeFromState = location.state?.claimCode || "";
  const claimCodeFromUrl = searchParams.get("claim_code") || "";
  const initialClaimCode = claimCodeFromState || claimCodeFromUrl;
  const returnTo = location.state?.returnTo;

  // Detect a referral code that will be attached at signup. Same precedence
  // as in handleSubmit: ?ref= → stored cookie/localStorage. Used solely to
  // render the "invite bonus detected" banner.
  const detectedReferralCode = (
    (searchParams.get("ref") || "").trim().toUpperCase()
    || getStoredReferralCode()
    || ""
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Hidden bot trap
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const pageLoadAtRef = useRef(Date.now());

  useEffect(() => { pageLoadAtRef.current = Date.now(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!turnstileToken) {
      setError("Please complete the human verification below.");
      return;
    }
    const submitDurationMs = Date.now() - pageLoadAtRef.current;
    setLoading(true);
    try {
      // Resolve referral code: ?ref= takes precedence, else stored cookie/localStorage.
      const refFromQuery = (searchParams.get("ref") || "").trim().toUpperCase();
      const referralCode = refFromQuery || getStoredReferralCode() || "";

      const result = await register(email, password, name, tenantName || undefined, initialClaimCode || undefined, {
        turnstile_token: turnstileToken,
        honeypot,
        submit_duration_ms: submitDurationMs,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });

      // Clear stored code after a successful signup so subsequent
      // registrations on the same browser don't accidentally re-attach.
      if (referralCode) {
        try { clearStoredReferralCode(); } catch { /* ignore */ }
      }

      // Always go to recovery codes page first
      navigate("/recovery-codes", {
        state: {
          recovery_codes: result?.recovery_codes || [],
          email: email,
          auto_claimed: result?.auto_claimed,
          returnTo: returnTo,
        },
        replace: true,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "blocked") {
        setError("Sign-up blocked for security reasons. If this is unexpected, please contact support.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Decorative Side (desktop only) */}
      <div className="hidden lg:flex flex-1 items-center justify-center auth-bg relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-100 via-zinc-200/50 to-zinc-300/30" />
        <div className="relative z-10 text-center px-12 space-y-6">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
            <Mail size={32} className="text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#111111]">
            Privacy by default.
          </h2>
          <p className="text-base text-[#888888] max-w-xs mx-auto">
            Your anonymous relay system. Multi-tenant, secure, and designed for scale.
          </p>
        </div>
      </div>

      {/* Form Side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#111111]" data-testid="register-title">
              Create account
            </h1>
            <p className="mt-4 text-base text-[#555555]">
              Set up your organization and start creating aliases.
            </p>
          </div>

          {initialClaimCode && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="register-claim-code-banner">
              <Key size={16} className="text-blue-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-blue-600">Your free alias will be automatically linked after registration.</p>
                <code className="text-xs font-mono font-medium text-blue-600">{initialClaimCode}</code>
              </div>
              <Check size={14} className="text-blue-600 shrink-0" />
            </div>
          )}

          {detectedReferralCode && (
            <div
              className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200"
              data-testid="register-referral-banner"
            >
              <Gift size={16} className="text-amber-600 shrink-0" />
              <p className="flex-1 text-xs text-amber-800">
                Invite bonus detected: you'll receive <span className="font-semibold">+1 day</span> after activation.
              </p>
              <code
                className="text-[10px] font-mono font-medium text-amber-700 bg-white/60 rounded px-1.5 py-0.5 shrink-0"
                data-testid="register-referral-code"
              >
                {detectedReferralCode}
              </code>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-600" data-testid="register-error">
              {error}
            </div>
          )}

          {/* Google Sign-In - above email/password */}
          <GoogleSignInButton
            claimCode={initialClaimCode}
            label={initialClaimCode ? "Sign up with Google & claim alias" : "Sign up with Google"}
          />

          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-zinc-500 font-mono uppercase">or</span>
            <Separator className="flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-[#555555]">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} strokeWidth={1.5} />
                <Input id="name" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} className="pl-10 h-12 sm:h-10" required data-testid="register-name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email" className="text-sm text-[#555555]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} strokeWidth={1.5} />
                <Input id="reg-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12 sm:h-10" required data-testid="register-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-password" className="text-sm text-[#555555]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} strokeWidth={1.5} />
                <Input id="reg-password" type="password" placeholder="Min. 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 h-12 sm:h-10" required minLength={8} data-testid="register-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="org" className="text-sm text-[#555555]">Organization <span className="text-zinc-500">(optional)</span></Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} strokeWidth={1.5} />
                <Input id="org" placeholder="Acme Corp" value={tenantName} onChange={(e) => setTenantName(e.target.value)} className="pl-10 h-12 sm:h-10" data-testid="register-org-input" />
              </div>
            </div>
            {/* Honeypot — hidden from humans, visible to bots */}
            <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }} aria-hidden="true">
              <label htmlFor="website_hp">Do not fill</label>
              <input
                id="website_hp"
                name="website_hp"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                data-testid="register-honeypot"
              />
            </div>
            <div data-testid="register-turnstile-container">
              <TurnstileWidget onToken={setTurnstileToken} />
            </div>
            <Button type="submit" className="w-full h-12 sm:h-10 bg-blue-600 hover:bg-blue-700 text-white font-medium" disabled={loading || !turnstileToken} data-testid="register-submit-button">
              {loading ? "Creating account..." : initialClaimCode ? "Create & claim alias" : "Create account"}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>

          <p className="text-sm text-zinc-500 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-600 hover:text-blue-600 font-medium" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
